// The counts the Drive and email section headers state.
//
// Derived from the SAME ``/uploads/tree`` payload those two trees render, never
// from ``/uploads/list``: the routes filter rows differently (the list drops
// superseded and re-upload-replaced rows, the tree keeps fold losers as dedup
// aliases), and on one live project they differ by 17 Drive files and 31
// threads. A header fed from the other endpoint states a number the list under
// it does not show.
//
// The "attached directly" section is NOT here for the same reason inverted: it
// renders the route's own list-derived array, so it counts that array directly.
import type { UploadsTreeEntry, UploadsTreeResponse } from "../api/hooks/useDriveSync";

// What counts as an item the project HAS, matching the backend's
// ``UploadStore._live_item_filter`` and the headline bar above. The trees also
// render tombstones and wrong-site quarantines - struck through, with their own
// affordances - and a dedup alias, which is a fold loser shown at its own Drive
// path. All three are worth SEEING and none of them is a file the project has,
// so none of them counts. Without this the section counts summed to more than
// the headline, and neither number was wrong about its own rows.
const isLive = (entry: UploadsTreeEntry): boolean =>
  entry.status === "active" && !entry.is_dedup_alias;

interface FilesComposition {
  driveFiles: number;
  /** One row each, standing for a whole conversation rather than a file. */
  threads: number;
  /** Summed across the threads that report one. Null when none do. */
  messages: number | null;
  /**
   * Whether EVERY thread reported. ``gmail_message_count`` is nullable with no
   * guaranteed backfill, so a sum over the ones that answered is a floor, not a
   * total - and rendered as a total it is the "reads as a precise figure and is
   * not one" failure. Rendered as "N+" when this is false.
   */
  messagesComplete: boolean;
  /** Attachments shown in the email section: under their thread, or a withheld one's. */
  threadAttachments: number;
}

export const summariseTree = (
  tree: UploadsTreeResponse | undefined,
): FilesComposition => {
  const threads = (tree?.gmail_sync_files ?? []).filter(isLive);
  let messages: number | null = null;
  let reporting = 0;
  let threadAttachments = 0;
  for (const thread of threads) {
    if (typeof thread.gmail_message_count === "number") {
      messages = (messages ?? 0) + thread.gmail_message_count;
      reporting += 1;
    }
    threadAttachments += (thread.nested_attachments ?? []).filter(isLive).length;
  }
  // Withheld conversations are not listed, but their kept attachments render in the
  // same email section, so the header counts them too.
  for (const group of tree?.withheld_thread_attachments ?? []) {
    threadAttachments += group.attachments.filter(isLive).length;
  }
  return {
    driveFiles: (tree?.drive_sync_files ?? []).filter(isLive).length,
    threads: threads.length,
    messages,
    messagesComplete: reporting === threads.length,
    threadAttachments,
  };
};

// "1,071" - a four-figure message count is unreadable without it.
export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;
